import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import {
  Users, Video, LineChart, IndianRupee, Plus, ArrowUpRight, TrendingUp,
  Sparkles, LayoutPanelLeft, Zap, BarChart3, Presentation, Palette,
  Calendar, Clock3, ExternalLink, Target, UserCheck, PhoneCall,
} from "lucide-react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const fmtRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((then - now) / 1000);
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (abs < 60) return rtf.format(diff, "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
};

const Delta = ({ value }) => {
  if (value === null || value === undefined) return null;
  const cls = value > 0 ? "vp-delta vp-delta-up" : value < 0 ? "vp-delta vp-delta-down" : "vp-delta vp-delta-flat";
  const sign = value > 0 ? "+" : "";
  return (
    <span className={cls}>
      <TrendingUp className={"w-3 h-3 " + (value < 0 ? "rotate-180" : "")} strokeWidth={2.5} />
      {sign}{value}%
    </span>
  );
};

export default function DashboardHome() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [crm, setCrm] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/webinars").then((r) => setWebinars(r.data || [])).catch(() => {});
    api.get("/crm/dashboard/widgets").then((r) => setCrm(r.data)).catch(() => {});
  }, []);

  // Pick next upcoming webinar (scheduled_at > now)
  const nextUp = webinars
    .filter((w) => new Date(w.scheduled_at).getTime() > Date.now())
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];

  const kpis = [
    { key: "upcoming", tid: TIDS.kpiUpcoming, label: "Upcoming webinars", value: summary?.upcoming_webinars ?? 0,
      icon: Video, tint: "#0B5CFF", bg: "rgba(11,92,255,0.12)", delta: summary?.delta_upcoming ?? null },
    { key: "regs", tid: TIDS.kpiRegistrations, label: "Total registrations", value: summary?.total_registrations ?? 0,
      icon: Users, tint: "#7C3AED", bg: "rgba(124,58,237,0.12)", delta: summary?.delta_registrations ?? null },
    { key: "attend", tid: TIDS.kpiAttendance, label: "Attendance", value: `${summary?.attendance_pct ?? 0}%`,
      icon: LineChart, tint: "#22D3EE", bg: "rgba(34,211,238,0.14)", delta: summary?.delta_attendance ?? null },
    { key: "rev", tid: TIDS.kpiRevenue, label: "Total revenue", value: fmtINR(summary?.total_revenue ?? 0),
      icon: IndianRupee, tint: "#1E8E3E", bg: "rgba(30,142,62,0.12)", delta: summary?.delta_revenue ?? null },
  ];

  const quickActions = [
    { to: "/app/webinars/new", icon: Video, label: "New webinar", tint: "#0B5CFF", bg: "rgba(11,92,255,0.10)" },
    { to: "/app/pages", icon: LayoutPanelLeft, label: "Landing page", tint: "#7C3AED", bg: "rgba(124,58,237,0.10)" },
    { to: "/app/ai", icon: Sparkles, label: "AI Studio", tint: "#EC4899", bg: "rgba(236,72,153,0.10)" },
    { to: "/app/presentations", icon: Presentation, label: "AI Slides", tint: "#EA580C", bg: "rgba(234,88,12,0.10)" },
    { to: "/app/studio", icon: Palette, label: "Thumbnail", tint: "#22D3EE", bg: "rgba(34,211,238,0.14)" },
    { to: "/app/automations", icon: Zap, label: "Automations", tint: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
    { to: "/app/analytics", icon: BarChart3, label: "Analytics", tint: "#1E8E3E", bg: "rgba(30,142,62,0.10)" },
    { to: "/app/registrants", icon: Users, label: "Registrants", tint: "#0B5CFF", bg: "rgba(11,92,255,0.10)" },
  ];

  const first = user?.name?.split(" ")[0] || "creator";
  const hourGreet = (() => {
    const h = new Date().getHours();
    if (h < 5) return "Late night hustle";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  })();

  return (
    <div className="space-y-8 vp-fade-up" data-testid="dashboard-home">
      {/* HERO BAND */}
      <section className="vp-dash-hero">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-bold uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
              {hourGreet}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-[1.05]">
              Hey {first} <span className="text-[#22D3EE]">.</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg mt-3 leading-relaxed">
              Aapka webinar business ek jagah — create, launch, grow. Yahan se sab manage karo.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/app/webinars/new"
              data-testid={TIDS.webinarNewBtn}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#050B24] font-bold text-sm hover:scale-[1.03] transition-transform"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} /> New webinar
            </Link>
            <Link
              to="/app/analytics"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 border border-white/25 text-white font-semibold text-sm hover:bg-white/20 transition-colors"
            >
              <BarChart3 className="w-4 h-4" strokeWidth={2} /> View analytics
            </Link>
          </div>
        </div>
      </section>

      {/* KPI GRID */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {kpis.map((k, i) => (
          <div key={k.key} data-testid={k.tid} className="vp-kpi vp-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center justify-between">
              <div className="vp-kpi-icon" style={{ background: k.bg }}>
                <k.icon className="w-5 h-5" style={{ color: k.tint }} strokeWidth={1.75} />
              </div>
              {k.delta !== null && k.delta !== undefined ? (
                <Delta value={k.delta} />
              ) : (
                <ArrowUpRight className="w-4 h-4 text-[#747680]" strokeWidth={1.75} />
              )}
            </div>
            <div className="mt-5 font-display text-3xl font-black tracking-tight">{k.value}</div>
            <div className="text-xs uppercase tracking-widest text-[#747680] font-bold mt-1">{k.label}</div>
          </div>
        ))}
      </div>

      {/* CRM AT A GLANCE */}
      <section data-testid="crm-widgets">
        <div className="flex items-center justify-between mb-3">
          <div className="vp-label">CRM at a glance</div>
          <Link to="/app/crm/leads" className="text-xs text-[#0B5CFF] hover:underline font-semibold">Open CRM →</Link>
        </div>
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          {/* Today's follow-ups */}
          <div className="vp-glass p-5" data-testid="crm-widget-followups">
            <div className="flex items-center justify-between">
              <div className="vp-kpi-icon" style={{ background: "rgba(245,158,11,0.14)" }}>
                <PhoneCall className="w-5 h-5" style={{ color: "#F59E0B" }} strokeWidth={1.75} />
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#747680]">Today</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-display text-3xl font-black tracking-tight">{crm?.today_followups_count ?? 0}</div>
              <div className="text-xs text-[#747680]">follow-ups</div>
            </div>
            <div className="mt-3 space-y-1.5">
              {(crm?.today_followups || []).slice(0, 3).map((l) => (
                <Link key={l.id} to={`/app/crm/leads`} className="flex items-center justify-between text-xs text-[#0B0D17] hover:bg-[#F6F7FA] rounded px-2 py-1.5 -mx-2" data-testid={`fu-item-${l.id}`}>
                  <span className="font-semibold truncate">{l.name}</span>
                  <span className="text-[#747680] shrink-0 ml-2">{l.mobile || l.email || ""}</span>
                </Link>
              ))}
              {(!crm?.today_followups || crm.today_followups.length === 0) && (
                <div className="text-xs text-[#747680] pt-1">Aaj koi follow-up nahi hai.</div>
              )}
            </div>
          </div>

          {/* New leads this week */}
          <div className="vp-glass p-5" data-testid="crm-widget-new-leads">
            <div className="flex items-center justify-between">
              <div className="vp-kpi-icon" style={{ background: "rgba(11,92,255,0.12)" }}>
                <Target className="w-5 h-5" style={{ color: "#0B5CFF" }} strokeWidth={1.75} />
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#747680]">This week</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-display text-3xl font-black tracking-tight">{crm?.new_leads_this_week ?? 0}</div>
              <div className="text-xs text-[#747680]">new leads</div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Link to="/app/crm/leads" className="text-xs text-[#0B5CFF] hover:underline font-semibold inline-flex items-center gap-1">
                Add lead <Plus className="w-3 h-3" />
              </Link>
              <span className="text-[#C8CCD6]">·</span>
              <Link to="/app/crm/clients" className="text-xs text-[#4A4D57] hover:underline inline-flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> Clients
              </Link>
            </div>
          </div>

          {/* Pipeline value */}
          <div className="vp-glass p-5" data-testid="crm-widget-pipeline">
            <div className="flex items-center justify-between">
              <div className="vp-kpi-icon" style={{ background: "rgba(30,142,62,0.12)" }}>
                <IndianRupee className="w-5 h-5" style={{ color: "#1E8E3E" }} strokeWidth={1.75} />
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#747680]">Pipeline</span>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-display text-3xl font-black tracking-tight">{fmtINR(crm?.pipeline_value ?? 0)}</div>
            </div>
            <div className="mt-3 text-xs text-[#747680] leading-relaxed">
              Open leads × interested service ka approximate revenue.
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACTIONS */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="vp-label">Quick actions</div>
          <div className="text-xs text-[#747680]">Ek click me kaam</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {quickActions.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              className="vp-quick-tile"
              data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: a.bg }}
              >
                <a.icon className="w-4 h-4" style={{ color: a.tint }} strokeWidth={1.75} />
              </div>
              <div className="font-semibold text-xs text-[#0B0D17] leading-tight">{a.label}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* MAIN GRID: Next up + Recent webinars / Activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Next up feature card */}
        <div className="vp-glass p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-black text-lg">Your webinars</div>
            <Link to="/app/webinars" className="text-sm text-[#0B5CFF] hover:underline font-semibold">View all →</Link>
          </div>

          {nextUp && (
            <div className="rounded-2xl bg-gradient-to-br from-[#0B5CFF]/8 via-white to-[#22D3EE]/8 border border-[#0B5CFF]/20 p-5 mb-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-[#0B5CFF]">Next up</div>
                  <div className="font-display font-black text-xl mt-1">{nextUp.title}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#4A4D57] flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(nextUp.scheduled_at).toLocaleString("en-IN")}</span>
                    <span className="inline-flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" /> {fmtRelative(nextUp.scheduled_at)}</span>
                  </div>
                </div>
                <Link
                  to={`/app/webinars/${nextUp.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0B5CFF] text-white text-xs font-bold hover:bg-[#0A4FD9] transition-colors"
                >
                  Open <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          )}

          {webinars.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-[#E4E6EC] rounded-2xl">
              <Video className="w-8 h-8 mx-auto text-[#C8CCD6]" strokeWidth={1.5} />
              <div className="font-semibold text-[#0B0D17] mt-3">No webinars yet</div>
              <div className="text-sm text-[#747680] mt-1">Apna pehla webinar 40 sec me launch karo</div>
              <Link to="/app/webinars/new" className="vp-btn-primary mt-4 inline-flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4" /> Create webinar
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E4E6EC]">
              {webinars.slice(0, 5).map((w) => (
                <Link
                  key={w.id}
                  to={`/app/webinars/${w.id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#F6F7FA] px-3 -mx-3 rounded-xl transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B0D17] truncate">{w.title}</div>
                    <div className="text-xs text-[#747680] mt-0.5 flex items-center gap-2">
                      <span>{new Date(w.scheduled_at).toLocaleString("en-IN")}</span>
                      <span className={
                        "px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase " +
                        (w.status === "live" ? "bg-red-100 text-red-700" :
                         w.status === "published" ? "bg-green-100 text-green-700" :
                         "bg-gray-100 text-gray-600")
                      }>
                        {w.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[#0B0D17] ml-3 shrink-0">
                    {w.price > 0 ? fmtINR(w.price) : <span className="text-[#1E8E3E]">Free</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Live activity */}
        <div className="vp-glass p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-black text-lg">Live activity</div>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#1E8E3E]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1E8E3E] animate-pulse" /> Live
            </span>
          </div>
          {(!summary?.activity || summary.activity.length === 0) ? (
            <div className="text-center py-8">
              <Users className="w-7 h-7 mx-auto text-[#C8CCD6]" strokeWidth={1.5} />
              <div className="text-sm text-[#747680] mt-3">Registrations will show up here in real time.</div>
            </div>
          ) : (
            <ul className="space-y-3.5">
              {summary.activity.slice(0, 8).map((a, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0B5CFF] to-[#22D3EE] flex items-center justify-center text-white text-xs font-black shrink-0">
                    {(a.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0B0D17]">
                      <span className="font-semibold">{a.name}</span>
                      <span className="text-[#4A4D57]"> registered</span>
                    </div>
                    <div className="text-xs text-[#747680] mt-0.5">{fmtRelative(a.at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
